import { join, basename } from 'node:path';
import { EventEmitter } from 'node:events';
import {
  DASHBOARD_PAGES_DIR,
  DASHBOARD_PLUGINS_DIR,
  DASHBOARD_MENU_PLUGINS_DIR,
  DASHBOARD_MODALS_DIR,
  EMBEDDED_SCRIPTS_DIR,
  SITE_WIDGETS_DIR,
  SITE_PLUGINS_DIR,
  SITE_COMPONENTS_DIR,
  PANELS_DIR,
} from '@wix/cli-app-definitions';
import chokidar from 'chokidar';
import _debounce from 'lodash/debounce.js';
import { deepEqual } from 'fast-equals';
import { WIX_CONFIG_FILENAME } from '@wix/cli-core-definitions';
import type { TypedEmitter } from './typed-emitter.js';
import type { ProjectModel } from '../model.js';
import { loadProjectModel } from '../model.js';
import {
  EMBEDDED_SCRIPTS_PARAMS_OVERRIDE_PATTERN,
  EMBEDDED_SCRIPTS_PATTERN,
} from '../dev-center/components/site/components.js';
import {
  API_PATTERN,
  EVENTS_PATTERN,
  SERVICE_PLUGINS_PATTERN,
  WEB_METHOD_PATTERN,
} from '../dev-center/components/backend/components.js';

export type ChangeType =
  | 'change-received'
  | 'component-updated'
  | 'model-updated'
  | 'model-did-not-change'
  | 'blocks-revision-changed';

// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
type ConfigEvents = {
  all: (model: ProjectModel, changeType: ChangeType) => void;
  error: (error: unknown) => void;
};

const DASHBOARD_PAGES_CONFIG_PATTERN = join(
  DASHBOARD_PAGES_DIR,
  '**',
  'page.json'
);

const DASHBOARD_PLUGINS_CONFIG_PATTERN = join(
  DASHBOARD_PLUGINS_DIR,
  '**',
  'plugin.json'
);

const DASHBOARD_MENU_PLUGINS_CONFIG_PATTERN = join(
  DASHBOARD_MENU_PLUGINS_DIR,
  '**',
  'plugin.json'
);

const DASHBOARD_MODALS_CONFIG_PATTERN = join(
  DASHBOARD_MODALS_DIR,
  '**',
  'modal.json'
);

const EMBEDDED_SCRIPTS_CONFIG_PATTERN = join(
  EMBEDDED_SCRIPTS_DIR,
  '**',
  'embedded.json'
);

const SITE_WIDGETS_CONFIG_PATTERN = join(
  SITE_WIDGETS_DIR,
  '**',
  'element.json'
);

const SITE_PLUGINS_CONFIG_PATTERN = join(SITE_PLUGINS_DIR, '**', 'plugin.json');

const SITE_COMPONENTS_CONFIG_PATTERN = join(
  SITE_COMPONENTS_DIR,
  '**',
  'component.json'
);

const SITE_COMPONENTS_ELEMENTS_CONFIG_PATTERN = join(
  SITE_COMPONENTS_DIR,
  '**',
  'elements',
  '**',
  'element.json'
);

const SITE_COMPONENTS_PANEL_CONFIG_PATTERN = join(
  SITE_COMPONENTS_DIR,
  '**',
  PANELS_DIR,
  '**',
  'panel.json'
);

export class ProjectModelWatcher {
  private readonly emitter = new EventEmitter() as TypedEmitter<ConfigEvents>;
  private readonly watchers: chokidar.FSWatcher[] = [];
  private prevModel: ProjectModel;

  constructor(initialModel: ProjectModel) {
    this.prevModel = initialModel;
  }

  watch() {
    const watcher = chokidar
      .watch(
        [
          DASHBOARD_PAGES_CONFIG_PATTERN,
          DASHBOARD_PLUGINS_CONFIG_PATTERN,
          DASHBOARD_MODALS_CONFIG_PATTERN,
          EMBEDDED_SCRIPTS_CONFIG_PATTERN,
          EMBEDDED_SCRIPTS_PATTERN,
          SITE_COMPONENTS_CONFIG_PATTERN,
          SITE_COMPONENTS_PANEL_CONFIG_PATTERN,
          SITE_WIDGETS_CONFIG_PATTERN,
          SITE_PLUGINS_CONFIG_PATTERN,
          SITE_COMPONENTS_ELEMENTS_CONFIG_PATTERN,
          DASHBOARD_MENU_PLUGINS_CONFIG_PATTERN,
          EMBEDDED_SCRIPTS_PARAMS_OVERRIDE_PATTERN,
          API_PATTERN,
          WEB_METHOD_PATTERN,
          EVENTS_PATTERN,
          SERVICE_PLUGINS_PATTERN,
          WIX_CONFIG_FILENAME,
        ],
        {
          /*
           * We use `useFsEvents: false` because of the following issue:
           * https://github.com/paulmillr/chokidar/issues/1285#issuecomment-2051731678
           */
          useFsEvents: false,
          ignoreInitial: true,
        }
      )
      .on('add', this.emitConfigUpdate)
      .on('unlink', this.emitConfigUpdate)
      .on('change', this.emitConfigUpdate);

    this.watchers.push(watcher);
    return this;
  }

  onChange(cb: (model: ProjectModel, changeType: ChangeType) => void) {
    this.emitter.on('all', cb);
    return this;
  }

  onError(cb: (error: unknown) => void) {
    this.emitter.on('error', cb);
    return this;
  }

  async close() {
    await Promise.all(this.watchers.map((watcher) => watcher.close()));
  }

  private emitConfigUpdate = _debounce(async (path: string) => {
    try {
      const model = await loadProjectModel(this.prevModel.projectFolder);
      this.emitter.emit('all', model, 'change-received');
      if (
        this.prevModel.config.blocks?.uiRevision !==
        model.config.blocks?.uiRevision
      ) {
        this.emitter.emit('all', model, 'blocks-revision-changed');
      }
      if (basename(path) === 'component.json') {
        this.emitter.emit('all', model, 'component-updated');
        return;
      }
      if (
        !deepEqual(model, this.prevModel) &&
        basename(path) === 'component.json'
      ) {
        this.prevModel = model;
        this.emitter.emit('all', model, 'component-updated');
      } else if (
        !deepEqual(model, this.prevModel) ||
        basename(path) === 'embedded.html' ||
        basename(path) === 'event.ts'
      ) {
        this.prevModel = model;
        this.emitter.emit('all', model, 'model-updated');
      } else {
        this.emitter.emit('all', model, 'model-did-not-change');
      }
    } catch (e) {
      this.emitter.emit('error', e);
    }
  }, 200);
}
